/* eslint-disable react/no-unstable-nested-components */
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, Button, Hidden, Toolbar } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import StartFreeButton from 'components/Login/StartFreeButton';
import EmailConfirmationBar from 'components/EmailConfirmationBar';
import { AnalyticsEventLocation } from 'services/analytics/entities';
import {
  useAuthStore,
  useTagsStore,
  useSongsStore,
  useSubscription,
} from 'services/hooks';
import { locations } from 'services/routes/locations';
import theme from 'theme';
import CompanyLogo from 'components/CompanyLogo';
import useAnalytics from 'services/hooks/analytics';
import { events } from 'services/analytics';
import MobileSideBarMenu from './components/Mobile/MobileSideBarMenu';
import NavigationLink from './components/NavigationLink';
import useStyles from './style';
import { StyledLogoContainer } from './styled';

export const displayHamburgerMenu = (logged: boolean, subscribed: boolean) => {
  if (!logged) return 'lg';
  if (logged && !subscribed) return 'desktop';
  return 'md';
};

const TopBar = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { authStore } = useAuthStore();
  const { isLoggedIn } = authStore;
  const { isSubscriptionActive } = useSubscription();
  const { track } = useAnalytics();

  const { areTagsOrFiltersApplied, resetTags } = useTagsStore();
  const { resetSongs } = useSongsStore();

  const [isOpen, setIsOpen] = useState(false);

  const handleLogoClick = (): void => {
    if (areTagsOrFiltersApplied) {
      resetTags();
      resetSongs();
    }
  };

  const handleTrackClick = useCallback(() => {
    track(
      events.upgradeSubscription(
        'navigation_bar',
        AnalyticsEventLocation.NAVIGATION_BAR
      )
    );
  }, [track]);

  const displayPricingButton = useMemo(() => {
    if (isLoggedIn) return null;
    return (
      <NavigationLink
        path={locations.subscription.pricing.pathname('')}
        label={t('general:topBar:pricing')}
        dataTestId="pricing-button"
      />
    );
  }, [isLoggedIn, t]);

  const displayIfLoggedIn = useMemo(() => {
    if (!isLoggedIn) return null;
    return (
      <>
        <NavigationLink
          path={locations.library.pathname}
          label={t('general:topBar:library')}
          dataTestId="library-button"
        />
        <NavigationLink
          path={locations.releases.pathname}
          label={t('general:topBar:releases')}
          dataTestId="releases-button"
        />
        <NavigationLink
          path={locations.account.main.pathname}
          dataTestId="account-icon"
          icon={(props) => (
            <AccountCircleOutlinedIcon {...props} className={classes.icon} />
          )}
        />
      </>
    );
  }, [classes.icon, isLoggedIn, t]);

  const displayIfNotLoggedIn = useMemo(() => {
    if (isLoggedIn) return null;
    return (
      <>
        <Box ml={5}>
          <Button
            component={Link}
            to={{ pathname: locations.general.login.pathname }}
            data-testid="sign-in-button"
            variant="outlined"
          >
            {t('auth:logIn').toUpperCase()}
          </Button>
        </Box>
        <Box ml={5}>
          <StartFreeButton />
        </Box>
      </>
    );
  }, [isLoggedIn, t]);

  const displayUpgradeButton = useMemo(() => {
    if (isLoggedIn && !isSubscriptionActive)
      return (
        <Button
          component={Link}
          variant="rounded"
          to={{ pathname: locations.subscription.pricing.pathname('') }}
          onClick={handleTrackClick}
          data-testid="upgrade"
          sx={{ ml: theme.spacing(5) }}
        >
          {t('general:topBar:upgrade').toUpperCase()}
        </Button>
      );
    return null;
  }, [isLoggedIn, isSubscriptionActive, handleTrackClick, t]);

  const displayRightSide = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down(
          displayHamburgerMenu(isLoggedIn, isSubscriptionActive)
        )]: { display: 'none' },
      }}
    >
      <NavigationLink
        path={locations.general.aiMusicGenerator.pathname}
        label={t('general:topBar:create')}
        dataTestId="create-button"
      />
      <NavigationLink
        path={locations.general.discovery.pathname}
        label={t('general:topBar:discoverPlaylists')}
        dataTestId="discover-playlists"
      />
      <NavigationLink
        path={locations.general.songList.pathname}
        label={t('general:topBar:templates')}
        dataTestId="templates-button"
      />
      {displayPricingButton}
      {displayIfLoggedIn}
      {displayIfNotLoggedIn}
      {displayUpgradeButton}
    </Box>
  );

  return (
    <>
      <AppBar
        position="static"
        color="inherit"
        sx={{
          backgroundImage: 'none !important',
          backgroundColor: theme.palette.grey[900],
          // necessary to display sidebar below app bar
          zIndex: isMobile ? theme.zIndex.drawer + 1 : theme.zIndex.drawer + 2,
          boxShadow: 'none',
        }}
        data-testid="top-bar"
      >
        <EmailConfirmationBar />
        <Toolbar className={classes.toolbar} key="left" data-testid="toolbar">
          {/* App Logo */}
          <StyledLogoContainer>
            <CompanyLogo onClick={handleLogoClick} />
          </StyledLogoContainer>
          {/* Hamburger Menu */}
          <Box
            sx={{
              display: 'none',
              [theme.breakpoints.down(
                displayHamburgerMenu(isLoggedIn, isSubscriptionActive)
              )]: { display: 'block' },
            }}
          >
            <Button
              data-testid="toggle-side-menu-button"
              onClick={() => setIsOpen(!isOpen)}
              color="inherit"
            >
              {isOpen ? (
                <CloseIcon fontSize="large" />
              ) : (
                <MenuIcon fontSize="large" />
              )}
            </Button>
          </Box>
          {/* Navbar Links */}
          {displayRightSide()}
        </Toolbar>
      </AppBar>
      {/* Drawer */}
      {/* @ts-ignore */}
      <Hidden only={[displayHamburgerMenu(isLoggedIn, isSubscriptionActive)]}>
        <MobileSideBarMenu isOpen={isOpen} setIsOpen={setIsOpen} />
      </Hidden>
    </>
  );
};

export default TopBar;
